import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { isValid } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExportService } from 'src/app/root/export.service';
import { LoadsForAllProductsService } from 'src/app/root/loads-for-all-products.service';
import { SnackBarService } from 'src/app/shared/snackbar.service';
import { BillingSummary } from 'src/app/types';
import { openSANDiBillingList } from 'src/app/utilities/openSandiBilling';

@Component({
  selector: 'lohi-billing',
  templateUrl: './billing.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingComponent implements OnInit {
  public bulkExporting = false;
  public taxExporting = false;

  public bulkExportFormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
  });
  public taxExportFormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
  });
  public loadsMissingPODCount$: Observable<number>;
  public loadsMissingPODCountMoreThan24Hours$: Observable<number>;
  public loadsAverageDaysToApprove$: Observable<number>;

  constructor(
    private exportService: ExportService,
    private logger: NGXLogger,
    private snackBar: SnackBarService,
    private loadsService: LoadsForAllProductsService,
    private cd: ChangeDetectorRef,
    private httpClient: HttpClient,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.loadsService.setCompletedLoadsShowFilters('missingPOD');

    this.loadsMissingPODCount$ = this.loadsService.completedLoadsPaginated$.pipe(
      map((value) => (value ? value.page.total : 0)),
    );

    this.loadsService.setCompletedLoadsShowFilters('missingPOD24Hrs');
    this.loadsMissingPODCountMoreThan24Hours$ = this.loadsService.completedLoadsPaginated$.pipe(
      map((value) => (value ? value.page.total : 0)),
    );

    this.loadsAverageDaysToApprove$ = this.loadsService.billingSummary$.pipe(map(loadsAverageDaysToApprove));
  }

  public async exportBulkData() {
    if (this.bulkExporting) {
      return;
    }
    if (this.bulkExportFormGroup.invalid) {
      this.bulkExportFormGroup.markAllAsTouched();
      return;
    }
    const value: { start: Date; end: Date } = this.bulkExportFormGroup.value;
    if (!isValid(value.start) || !isValid(value.end)) {
      return;
    }
    this.bulkExporting = true;
    try {
      const urls = await this.exportService.getBulkExportUrl$(value.start, value.end).toPromise();
      for (const url of urls) {
        window.open(url);
      }
      this.resetBulkExportForm();
      this.bulkExportFormGroup.markAsUntouched();
    } catch (error) {
      this.logger.error(error);
      this.snackBar.showError('Failed to retrieve export');
    }
    this.bulkExporting = false;
  }

  public resetBulkExportForm() {
    this.bulkExportFormGroup = new FormGroup({
      start: new FormControl(null, [Validators.required]),
      end: new FormControl(null, [Validators.required]),
    });
    this.cd.markForCheck();
  }

  public async exportTaxData() {
    if (this.taxExporting) {
      return;
    }
    if (this.taxExportFormGroup.invalid) {
      this.taxExportFormGroup.markAllAsTouched();
      return;
    }
    const value: { start: Date; end: Date } = this.taxExportFormGroup.value;
    if (!isValid(value.start) || !isValid(value.end)) {
      return;
    }
    this.taxExporting = true;
    try {
      const url = await this.exportService.getCompanyTaxExportUrl$(value.start, value.end).toPromise();
      window.open(url);

      this.resetTaxExportForm();
      this.taxExportFormGroup.markAsUntouched();
    } catch (error) {
      this.logger.error(error);
      this.snackBar.showError('Failed to retrieve export');
    }
    this.taxExporting = false;
  }

  public resetTaxExportForm() {
    this.taxExportFormGroup = new FormGroup({
      start: new FormControl(null, [Validators.required]),
      end: new FormControl(null, [Validators.required]),
    });
    this.cd.markForCheck();
  }

  public goToSandiBilling() {
    openSANDiBillingList(this.httpClient, this.logger);
  }

  public navigateToLoadsList(filterValue: string) {
    this.router.navigate(['v2/loads/list/completed'], {
      queryParams: {
        [filterValue]: true,
      },
    });
  }
}

function loadsAverageDaysToApprove(summary: BillingSummary): number {
  return summary.data.averageWorkingDaysToApprove ?? 0;
}
